import { clearDragItem, readDragItem, writeDragItem, type BlockPreset } from '@/components/builder/drag-data';
import {
  NodeContextMenu,
  NodeSelectionToolbar,
  type ContextMenuState,
  type NodeActionHandlers,
} from '@/components/builder/node-actions';
import { cn } from '@/lib/cn';
import {
  type AuthoringDoc,
  collectIds,
  resolveDropTarget,
  type DropTarget,
  type Frame,
} from '@personalizehq/editor';
import type { BlockNode, ExperienceDocument, Viewport } from '@personalizehq/schemas/ir';
import { mount, type CustomComponent, type LayoutSnapshot, type MountHandle } from '@personalizehq/renderer-web';
import { GripVertical } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

// The canvas. It is the PRODUCTION renderer — not a preview approximation — so what the author
// sees is what the SDK will draw, down to the wrap points.
//
// Three things here are load-bearing and easy to "simplify" back into bugs:
//
// 1. `contain: paint` on the frame. The renderer's overlay host is `position: fixed; inset: 0` —
//    correct in a customer's app, catastrophic here, where it would cover the whole dashboard.
//    `contain` makes this div the containing block for that fixed child, so the overlay resolves to
//    exactly the artboard's box. (Over `transform`: same containing-block effect, plus clipping,
//    without transform's text-rasterisation side effects. A `transform: scale()` for zoom in E4
//    then composes with it.)
// 2. `onDismiss` is a no-op. Esc, a scrim click and any `{type:'dismiss'}` button all funnel
//    through the renderer's one dismiss closure, whose default is `destroy()`. An author pressing
//    Esc would otherwise delete their own canvas.
// 3. Every viewport field is PINNED. The renderer defaults `font_scale` from the DASHBOARD's root
//    font size and `direction` from the DASHBOARD's `dir` — leak either and the artboard silently
//    stops showing what the device will.

/** The solver emits nodes the DOCUMENT does not contain: `repeat` expansion writes `${id}__${i}`
 *  and `progress` writes `${id}_seg${i}` (measure.ts). `nodeAt()` hands those back, so selecting
 *  one would select a node that does not exist. Map back to the authored node instead. */
export function toDocId(id: string): string {
  return id.replace(/__\d+$/, '').replace(/_seg\d+$/, '');
}

/** THE EMPTY-CONTAINER PROBLEM. An empty `stack`/`box`/`scroll`/`grid` measures to nothing — hug
 *  content with no content is zero — so a container the author just inserted is INVISIBLE and, worse,
 *  un-hittable: `nodeAt()` can never return a node whose frame has no area. Without this the palette
 *  appears broken ("I clicked stack and nothing happened"), and E2's drag-insert would have nowhere
 *  to drop.
 *
 *  The placeholder is drawn in the EDITOR'S OVERLAY, never in `renderer-web` — the renderer must keep
 *  rendering exactly what the device renders, or the canvas stops being pixel-honest, which is the
 *  whole reason it runs the production renderer. The overlay is also where its click target lives. */
const EMPTY_MIN_W = 64;
const EMPTY_MIN_H = 40;
const INLINE_SLOT_TOP = 156;
const ACTION_TOOLBAR_W = 96;
const ACTION_TOOLBAR_H = 36;
const ACTION_TOOLBAR_GAP = 8;

function selectionToolbarPosition(
  viewport: Viewport,
  origin: { x: number; y: number },
  selection: readonly string[],
  frames: Readonly<Record<string, Frame>>
): React.CSSProperties {
  const selectedFrames = selection.flatMap((id) => frames[id] ? [frames[id]!] : []);
  if (selectedFrames.length === 0) return { left: 4, top: 4 };
  const localX = Math.min(...selectedFrames.map(([x]) => x));
  const localY = Math.min(...selectedFrames.map(([, y]) => y));
  const right = Math.max(...selectedFrames.map(([x, , width]) => x + width));
  const bottom = Math.max(...selectedFrames.map(([, y, , height]) => y + height));
  const width = right - localX;
  const height = bottom - localY;
  const x = origin.x + localX;
  const y = origin.y + localY;

  const containsSelected = ([fx, fy, fw, fh]: Frame): boolean =>
    fx <= localX && fy <= localY && fx + fw >= localX + width && fy + fh >= localY + height;
  const blockers = Object.entries(frames)
    .filter(([id, frame]) => !selection.includes(id) && !containsSelected(frame))
    .map(([, [fx, fy, fw, fh]]) => ({ x: origin.x + fx, y: origin.y + fy, width: fw, height: fh }));
  const candidates = [
    { left: x, top: y - ACTION_TOOLBAR_H - ACTION_TOOLBAR_GAP },
    { left: x, top: y + height + ACTION_TOOLBAR_GAP },
    { left: x + width + ACTION_TOOLBAR_GAP, top: y },
    { left: x - ACTION_TOOLBAR_W - ACTION_TOOLBAR_GAP, top: y },
    // Dense stacks can occupy every edge around a short text node. Prefer a quiet artboard corner
    // over covering a sibling; the selection outline keeps the action's target unambiguous.
    { left: 12, top: viewport.h - ACTION_TOOLBAR_H - 12 },
    { left: viewport.w - ACTION_TOOLBAR_W - 12, top: viewport.h - ACTION_TOOLBAR_H - 12 },
    { left: 12, top: 12 },
    { left: viewport.w - ACTION_TOOLBAR_W - 12, top: 12 },
  ];
  const fits = ({ left, top }: { left: number; top: number }): boolean =>
    left >= 4 && top >= 4 && left + ACTION_TOOLBAR_W <= viewport.w - 4 && top + ACTION_TOOLBAR_H <= viewport.h - 4;
  const clear = ({ left, top }: { left: number; top: number }): boolean =>
    blockers.every(
      (frame) =>
        left + ACTION_TOOLBAR_W <= frame.x ||
        left >= frame.x + frame.width ||
        top + ACTION_TOOLBAR_H <= frame.y ||
        top >= frame.y + frame.height
    );
  const best = candidates.find((candidate) => fits(candidate) && clear(candidate));
  if (best) return best;
  return {
    left: Math.max(4, Math.min(viewport.w - ACTION_TOOLBAR_W - 4, x)),
    top: Math.max(4, Math.min(viewport.h - ACTION_TOOLBAR_H - 4, y + height + ACTION_TOOLBAR_GAP)),
  };
}

function emptyContainers(root: BlockNode): Map<string, string> {
  const found = new Map<string, string>();
  const walk = (node: BlockNode): void => {
    if ('children' in node) {
      if (node.children.length === 0) found.set(node.id, node.block);
      else node.children.forEach(walk);
    }
  };
  walk(root);
  return found;
}

/** F1 — THE CANVAS MUST REGISTER EVERY `custom`. `resolve()` DROPS a `custom` whose identifier is
 *  not in the host registry (SPEC §7.1, correct on a device) — and the artboard registered NOTHING,
 *  so every `custom` was invisible, un-hittable and contributed 0pt, while being perfectly real in
 *  the tree, the inspector and the database. The canvas was showing a DIFFERENT layout from the one
 *  that ships, in the direction that hides the author's work.
 *
 *  The fix is an editor-only STUB painter for each identifier the document uses: a labelled grey box.
 *  A `custom` contributes NO intrinsic size (SPEC §8) — its box is the AUTHORED size — so registering
 *  a stub makes the geometry match the device exactly; only the contents are a placeholder. The same
 *  reason `newBlock` ships a grey placeholder image. */
export function customIdentifiers(root: BlockNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: BlockNode): void => {
    if (n.block === 'custom') ids.add(n.identifier);
    if ('children' in n) n.children.forEach(walk);
    // A repeat's TEMPLATE too — expand() clones it into children (same identifier, fresh ids), so a
    // custom that lives there needs its stub registered like any other. Same walk set as
    // `collectIds` in @personalizehq/editor: children + fallback + template are all real wire nodes.
    if (n.block === 'repeat') walk(n.template);
    if (n.fallback) walk(n.fallback);
  };
  walk(root);
  return ids;
}

/** A grey labelled box that fills the custom's solved rect. One per identifier, so it can name it. */
const stubPainter = (identifier: string): CustomComponent => (el) => {
  el.style.cssText =
    'display:flex;align-items:center;justify-content:center;box-sizing:border-box;width:100%;height:100%;' +
    'padding:4px;border:1px dashed #9ca3af;background:#f3f4f6;color:#6b7280;' +
    'font:500 11px/1.2 system-ui,sans-serif;text-align:center;overflow:hidden;';
  el.textContent = `custom: ${identifier}`;
};

interface ArtboardProps {
  doc: ExperienceDocument;
  viewport: Viewport;
  selection: readonly string[];
  selectionAnchor: string | null;
  onSelect: (id: string | null, mode?: 'replace' | 'toggle' | 'range') => void;
  /** asset id → URL. Fonts are BUNDLED (SPEC §4.1): a font token's `src` is an asset id, and
   *  without this map the renderer measures with a fallback family — every wrap point moves. */
  assets?: Record<string, string> | undefined;
  /** Wireframe every node's rect. The rects are the solver's, so this costs one loop. */
  outlines?: boolean;
  onMove?: ((id: string, parentId: string, index: number) => void) | undefined;
  onInsert?: ((preset: BlockPreset, parentId: string, index: number) => void) | undefined;
  /** Shared human mutations for the selection toolbar and point-anchored context menu. */
  actions?: NodeActionHandlers | undefined;
  labels?: AuthoringDoc['labels'] | undefined;
  /** Editor-only host context. It never enters the renderer or Experience IR. */
  inlineSlot?: string | undefined;
  /** Active app platform, used only to keep the representative host chrome honest. */
  hostPlatform?: string | undefined;
  /** THE SELECTED NODE'S SOLVED RECT, reported up so the inspector can say what `fill` and `hug`
   *  actually resolved to. Only the ONE frame, never the map: the map changes on every keystroke and
   *  would re-render the whole panel for nodes nobody is looking at. */
  onSelectedFrame?: ((frame: Frame | null) => void) | undefined;
  /** The rendered CONTENT HEIGHT — the root/document box height from the solver, reported up so the
   *  viewport toolbar can show a fit-to-content read-out. It is exactly what index.ts sizes the
   *  surface LAYER from, so it cannot disagree with what was painted. */
  onContentHeight?: ((height: number) => void) | undefined;
}

export function Artboard({
  doc,
  viewport,
  selection,
  selectionAnchor,
  onSelect,
  onMove,
  onInsert,
  actions,
  labels = {},
  inlineSlot,
  hostPlatform,
  assets,
  outlines,
  onSelectedFrame,
  onContentHeight,
}: ArtboardProps): React.JSX.Element {
  const frameRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<MountHandle | null>(null);
  const [nodes, setNodes] = useState<LayoutSnapshot['nodes']>([]);
  const [hover, setHover] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  /** Where the LAYER sits inside the frame. Snapshot coordinates are layer-relative, and the layer
   *  is only the document's box — a bottom sheet's is pinned to the bottom, a modal's is centered.
   *  So this offset is what converts between the two spaces, for hit-testing and for outlines. */
  const [origin, setOrigin] = useState({ x: 0, y: 0 });

  /** Re-queried, never cached: switching `surface` destroys the shadow root and builds a new one. */
  const layerEl = (): HTMLElement | null =>
    frameRef.current?.querySelector<HTMLElement>('[data-phq-host]')?.shadowRoot?.querySelector('[data-phq-layer]') ?? null;

  /** SCALE-AWARE (F12 zoom). The artboard may sit inside a `transform: scale()` wrapper, so
   *  `getBoundingClientRect` returns POST-transform screen px while the solver's rects — and the
   *  overlay drawn from them — are in the layer's own unscaled space. The layer is always
   *  viewport-WIDE, so `box.width / viewport.w` IS the scale. Everything read off a bounding rect is
   *  divided back into layer space; at zoom 1 the divisor is 1 and this is a no-op. */
  const scaleOf = (layer: HTMLElement): number => layer.getBoundingClientRect().width / viewport.w || 1;

  const sync = (handle: MountHandle): void => {
    setNodes(handle.snapshot().nodes);
    const frame = frameRef.current;
    const layer = layerEl();
    if (!frame || !layer) return;
    // Inline is the one surface the production renderer deliberately gives NO chrome. In the
    // editor only, place its layer into a representative host slot. Snapshot coordinates remain
    // layer-relative and `origin` below carries the offset into selection/DnD hit-testing.
    if (doc.surface === 'inline') layer.style.top = `${INLINE_SLOT_TOP}px`;
    const f = frame.getBoundingClientRect();
    const l = layer.getBoundingClientRect();
    const scale = scaleOf(layer);
    setOrigin({ x: (l.left - f.left) / scale, y: (l.top - f.top) / scale });
  };

  /** F1: a stub painter per `custom` identifier in the document, so `resolve()` keeps them and the
   *  canvas geometry matches the device. Rebuilt only when the SET of identifiers changes (a plain
   *  string key, so an unrelated edit does not churn the registry and remount every custom). */
  const customKey = [...customIdentifiers(doc.root)].sort().join(' ');
  const registry = useMemo(() => {
    const reg: Record<string, CustomComponent> = {};
    for (const id of customIdentifiers(doc.root)) reg[id] = stubPainter(id);
    return reg;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on customKey (the identifier set), not the doc object
  }, [customKey]);

  // Mount ONCE. Everything after is `update()` — remounting per edit would drop the shadow root,
  // re-register fonts and flash the canvas on every keystroke.
  useEffect(() => {
    const handle = mount(doc, {
      container: frameRef.current as HTMLElement,
      viewport,
      registry,
      ...(assets ? { assets } : {}),
      onDismiss: () => {},
    });
    handleRef.current = handle;
    void handle.ready.then(() => sync(handle));

    return () => {
      handle.destroy();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once; the effect below updates.
  }, []);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    // ponytail: no debounce. `update()` is a full repaint bounded by MAX_LAYOUT_NODES=1024 — a few
    // ms — and React already coalesces to one render per keystroke. Add a rAF only if a profile says so.
    handle.update(doc, { viewport, registry, ...(assets ? { assets } : {}) });
    sync(handle);
    // `sync` is intentionally render-local: it reads this render's document surface and current
    // DOM geometry after `update`. Its real reactive inputs are already listed below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, viewport, assets, registry]);

  const layerPoint = (e: { clientX: number; clientY: number }): { x: number; y: number } | null => {
    const layer = layerEl();
    if (!layer) return null;
    const box = layer.getBoundingClientRect();
    const scale = scaleOf(layer);
    return { x: (e.clientX - box.left) / scale, y: (e.clientY - box.top) / scale };
  };

  const at = (e: { clientX: number; clientY: number }): string | null => {
    const handle = handleRef.current;
    const point = layerPoint(e);
    if (!handle || !point) return null;
    // Measured live, not from `origin`: a click can land in the same frame as the edit that moved
    // the layer, and a one-render-stale offset selects the wrong node. Divided by the zoom scale so
    // the click maps back into the layer's own (unscaled) coordinate space.
    const id = handle.nodeAt(point.x, point.y);
    return id === null ? null : toDocId(id);
  };

  /** An empty container HAS a frame — it is just a zero-area one, sitting exactly where the
   *  placeholder should go. So the solver still tells us where to draw it. */
  const empties = useMemo(() => {
    const found = emptyContainers(doc.root);
    return nodes
      .filter((node) => found.has(node.id))
      .map((node) => ({ id: node.id, block: found.get(node.id)!, frame: node.frame }));
  }, [doc, nodes]);

  const frames = useMemo<Record<string, Frame>>(() => {
    const result: Record<string, Frame> = {};
    const authoredIds = collectIds(doc.root);
    for (const node of nodes) {
      const id = toDocId(node.id);
      if (authoredIds.has(id)) result[id] = node.frame;
    }
    for (const empty of empties) {
      const [x, y, w, h] = empty.frame;
      result[empty.id] = [x, y, Math.max(w, EMPTY_MIN_W), Math.max(h, EMPTY_MIN_H)];
    }
    return result;
  }, [doc.root, empties, nodes]);

  // REPORT THE SELECTED FRAME UP. Keyed on the frame's VALUES, not its identity: `frames` is rebuilt
  // every solve (every keystroke), so an identity-keyed effect would fire constantly and re-render
  // the inspector for a rect that never moved.
  const selectedFrame = selectionAnchor === null ? undefined : frames[selectionAnchor];
  const frameKey = selectedFrame ? selectedFrame.join(',') : '';
  useEffect(() => {
    onSelectedFrame?.(selectedFrame ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the frame's VALUES
  }, [frameKey]);

  // REPORT THE CONTENT HEIGHT UP. Keyed on the numeric height so it fires only when the document box
  // actually changes size — not on every keystroke that leaves the height untouched.
  const contentHeight = nodes[0]?.frame[3] ?? null;
  useEffect(() => {
    if (contentHeight !== null) onContentHeight?.(contentHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the numeric height
  }, [contentHeight]);

  const targetAt = (
    e: { clientX: number; clientY: number },
    item: ReturnType<typeof readDragItem>
  ): DropTarget | null => {
    const pointer = layerPoint(e);
    if (!item || !pointer) return null;
    return resolveDropTarget({
      root: doc.root,
      frames,
      pointer,
      ...(item.type === 'node' ? { draggedId: item.id } : {}),
    });
  };

  const outline = (id: string | null, className: string): React.JSX.Element | null => {
    const node = id === null ? undefined : nodes.find((n) => n.id === id);
    if (!node) return null;
    const [x, y, w, h] = node.frame;
    return <div className={className} style={{ left: origin.x + x, top: origin.y + y, width: w, height: h }} />;
  };

  return (
    <div
      role="region"
      aria-label="Authoring canvas"
      tabIndex={0}
      className="relative shrink-0 shadow-2xl"
      style={{ width: viewport.w, height: viewport.h }}
      onClickCapture={(e) => {
        // Runtime actions belong to Preview. Actionable renderer nodes stop propagation themselves,
        // so authoring selection must intercept clicks before they reach the shadow-root listener.
        if (e.target instanceof Node && frameRef.current?.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          onSelect(at(e), e.shiftKey ? 'range' : e.metaKey || e.ctrlKey ? 'toggle' : 'replace');
        }
      }}
      onClick={(e) => onSelect(at(e), e.shiftKey ? 'range' : e.metaKey || e.ctrlKey ? 'toggle' : 'replace')}
      onContextMenu={(e) => {
        if (!actions) return;
        const id = at(e);
        if (!id) return;
        e.preventDefault();
        if (!selection.includes(id)) onSelect(id);
        setContextMenu({ id, x: e.clientX, y: e.clientY });
      }}
      onKeyDown={(e) => {
        if (!actions || !selectionAnchor || !frames[selectionAnchor]) return;
        if (e.key !== 'ContextMenu' && !(e.shiftKey && e.key === 'F10')) return;
        e.preventDefault();
        const [x, y, w, h] = frames[selectionAnchor]!;
        const box = e.currentTarget.getBoundingClientRect();
        const scale = box.width / viewport.w || 1;
        setContextMenu({
          id: selectionAnchor,
          x: box.left + (origin.x + x + w / 2) * scale,
          y: box.top + (origin.y + y + h) * scale,
        });
      }}
      onMouseMove={(e) => setHover(at(e))}
      onMouseLeave={() => setHover(null)}
      onDragOver={(e) => {
        const item = readDragItem(e.dataTransfer);
        const target = targetAt(e, item);
        if (!item || !target) {
          setDropTarget(null);
          return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = item.type === 'node' ? 'move' : 'copy';
        setDropTarget(target);
      }}
      onDragLeave={(e) => {
        if (!(e.relatedTarget instanceof Node) || !e.currentTarget.contains(e.relatedTarget)) {
          setDropTarget(null);
        }
      }}
      onDrop={(e) => {
        const item = readDragItem(e.dataTransfer);
        const target = targetAt(e, item);
        e.preventDefault();
        if (item && target) {
          if (item.type === 'node') onMove?.(item.id, target.parentId, target.index);
          else onInsert?.(item.preset, target.parentId, target.index);
        }
        clearDragItem();
        setDropTarget(null);
      }}
    >
      {/* The simulated device screen the overlay sits on. */}
      <div
        ref={frameRef}
        className="size-full overflow-hidden rounded-[28px]"
        style={{
          position: 'relative',
          contain: 'paint',
          background: viewport.color_scheme === 'dark' ? '#09090b' : '#ffffff',
        }}
      >
        {doc.surface === 'inline' ? (
          <InlineHostScreen
            slot={inlineSlot}
            scheme={viewport.color_scheme}
            platform={hostPlatform}
            contentHeight={frames[doc.root.id]?.[3]}
            screenHeight={viewport.h}
          />
        ) : null}
      </div>

      {/* Outlines live OUTSIDE the frame: inside it, the overlay host's z-index (2147483000) wins.
          Positioned straight from the solver's rects — never re-derived from the DOM. And never a
          resize handle: size is an inspector property, handles would imply freeform. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[28px]">
        {outlines
          ? nodes.map((node) => (
              <div
                key={node.id}
                className="absolute border border-indigo-400/30"
                style={{
                  left: origin.x + node.frame[0],
                  top: origin.y + node.frame[1],
                  width: node.frame[2],
                  height: node.frame[3],
                }}
              />
            ))
          : null}

        {empties.map(({ id, block, frame }) => (
          <button
            key={id}
            type="button"
            // The one thing on this overlay that IS a click target — because the node it stands for
            // has no area, so the renderer's own hit-test can never find it.
            className="pointer-events-auto absolute flex items-center justify-center rounded border border-dashed border-indigo-400/80 bg-indigo-400/5 text-[10px] text-indigo-500"
            style={{
              left: origin.x + frame[0],
              top: origin.y + frame[1],
              width: Math.max(frame[2], EMPTY_MIN_W),
              height: Math.max(frame[3], EMPTY_MIN_H),
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(id, e.shiftKey ? 'range' : e.metaKey || e.ctrlKey ? 'toggle' : 'replace');
            }}
            draggable={id !== doc.root.id}
            onDragStart={(e) => writeDragItem(e.dataTransfer, { type: 'node', id })}
            onDragEnd={() => {
              clearDragItem();
              setDropTarget(null);
            }}
          >
            empty {block}
          </button>
        ))}

        {hover !== null && !selection.includes(hover)
          ? outline(hover, 'absolute border border-indigo-400/70')
          : null}
        {selectionAnchor !== null && selectionAnchor !== doc.root.id ? (() => {
          const selected = nodes.find((node) => node.id === selectionAnchor);
          if (!selected) return null;
          const [x, y, w, h] = selected.frame;
          return (
            <div
              className="absolute border-2 border-indigo-500"
              style={{ left: origin.x + x, top: origin.y + y, width: w, height: h }}
            >
              <span
                draggable
                title="Drag block"
                className="pointer-events-auto absolute right-0 top-1/2 grid size-5 translate-x-1/2 -translate-y-1/2 cursor-grab place-items-center rounded bg-indigo-500 text-white shadow active:cursor-grabbing"
                onDragStart={(e) => writeDragItem(e.dataTransfer, { type: 'node', id: selectionAnchor })}
                onDragEnd={() => {
                  clearDragItem();
                  setDropTarget(null);
                }}
              >
                <GripVertical className="size-3" />
              </span>
            </div>
          );
        })() : null}
        {selection.filter((id) => id !== selectionAnchor).map((id) => <div key={id}>{outline(id, 'absolute border-2 border-indigo-500')}</div>)}
        {selectionAnchor && actions && frames[selectionAnchor] ? (
          <NodeSelectionToolbar
            root={doc.root}
            id={selectionAnchor}
            handlers={actions}
            labels={labels}
            style={selectionToolbarPosition(viewport, origin, selection, frames)}
            onMore={(anchor) => setContextMenu({ id: selectionAnchor, x: anchor.left, y: anchor.bottom + 4 })}
          />
        ) : null}
        {dropTarget ? (
          <div
            className="absolute bg-indigo-500 shadow-[0_0_0_1px_rgba(255,255,255,0.8)]"
            style={{
              left: origin.x + dropTarget.indicator[0],
              top: origin.y + dropTarget.indicator[1],
              width: Math.max(dropTarget.indicator[2], 2),
              height: Math.max(dropTarget.indicator[3], 2),
            }}
          />
        ) : null}
      </div>
      {actions ? (
        <NodeContextMenu
          state={contextMenu}
          root={doc.root}
          labels={labels}
          handlers={actions}
          onClose={() => setContextMenu(null)}
        />
      ) : null}
    </div>
  );
}

function InlineHostScreen({
  slot,
  scheme,
  platform,
  contentHeight,
  screenHeight,
}: {
  slot?: string | undefined;
  scheme: Viewport['color_scheme'];
  platform?: string | undefined;
  contentHeight?: number | undefined;
  screenHeight: number;
}): React.JSX.Element {
  const dark = scheme === 'dark';
  const platformLabel =
    platform === 'ios'
      ? 'iOS'
      : platform === 'android'
        ? 'Android'
        : platform
          ? platform.replace('_', ' ')
          : 'mobile';
  const slotHeight = Math.max(54, Math.min(contentHeight ?? 54, screenHeight - INLINE_SLOT_TOP - 32));
  const feedTop = INLINE_SLOT_TOP + slotHeight + 24;
  return (
    <div
      data-inline-host-screen=""
      aria-label={`Representative ${platformLabel} host screen${slot ? `, slot ${slot}` : ', unlinked inline slot'}`}
      className={cn(
        'pointer-events-none absolute inset-0 overflow-hidden rounded-[28px]',
        dark ? 'bg-zinc-950 text-zinc-100' : 'bg-zinc-50 text-zinc-900'
      )}
    >
      <div className="flex h-7 items-center justify-between px-5 text-[9px] font-semibold">
        <span>9:41</span>
        <span className="tracking-widest">{platform === 'android' ? '◁ ○ ▢' : '● ◒ ▰'}</span>
      </div>
      <div className="flex h-16 items-center gap-3 border-b border-current/10 px-5">
        <div className="size-8 rounded-full bg-indigo-500/20" />
        <div className="space-y-1.5">
          <div className="h-2.5 w-24 rounded bg-current/20" />
          <div className="h-2 w-16 rounded bg-current/10" />
        </div>
      </div>
      <div
        data-inline-slot=""
        className="absolute inset-x-3 rounded-md border border-dashed border-indigo-400/70"
        style={{ top: INLINE_SLOT_TOP - 5, height: slotHeight + 10 }}
      >
        <span className="absolute bottom-full left-1 pb-1 text-[9px] font-medium text-indigo-500">
          {slot ? `Host app · “${slot}”` : 'Unlinked inline slot'}
        </span>
      </div>
      <div className="absolute inset-x-5 space-y-3" style={{ top: feedTop }}>
        <div className="h-20 rounded-xl bg-current/5" />
        <div className="h-20 rounded-xl bg-current/5" />
        <div className="mx-auto h-1 w-28 rounded bg-current/20" />
      </div>
    </div>
  );
}
